import React, { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import {
  Task,
  TaskPriority,
  TaskStatus,
  Project,
  Subtask,
} from "../../types/Work";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  Undo,
  Heading,
  List,
  Link,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";

interface TaskModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (task: Omit<Task, "id" | "createdAt" | "updatedAt">) => void;
  task?: Task;
  projects: Project[];
  defaultStatus?: TaskStatus;
}

const ESTIMATION_SHORTCUTS = [
  { label: "10 min", minutes: 10 },
  { label: "30 min", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "2 hours", minutes: 120 },
  { label: "4 hours", minutes: 240 },
  { label: "1 day", minutes: 480 },
];

export const TaskModal: React.FC<TaskModalProps> = ({
  show,
  onHide,
  onSave,
  task,
  projects,
  defaultStatus = "todo",
}) => {
  const [name, setName] = useState(task?.name || "");
  const [descriptionHtml, setDescriptionHtml] = useState(
    task?.descriptionHtml || ""
  );
  const [priority, setPriority] = useState<TaskPriority>(
    task?.priority || "medium"
  );
  const [projectId, setProjectId] = useState<string | null>(
    task?.projectId || null
  );
  const [estimationMinutes, setEstimationMinutes] = useState<number | string>(
    task?.estimationMinutes || "",
  );
  const [subtasks, setSubtasks] = useState<Subtask[]>(task?.subtasks || []);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");

  React.useEffect(() => {
    if (task) {
      setName(task.name);
      setDescriptionHtml(task.descriptionHtml);
      setPriority(task.priority);
      setProjectId(task.projectId);
      setEstimationMinutes(task.estimationMinutes || "");
      setSubtasks(task.subtasks);
    } else {
      setName("");
      setDescriptionHtml("");
      setPriority("medium");
      setProjectId(null);
      setEstimationMinutes("");
      setSubtasks([]);
    }
  }, [task, show]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const estimationValue =
      typeof estimationMinutes === "string"
        ? parseInt(estimationMinutes) || undefined
        : estimationMinutes > 0
          ? estimationMinutes
          : undefined;

    onSave({
      name: name.trim(),
      descriptionHtml,
      priority,
      projectId,
      estimationMinutes: estimationValue,
      subtasks,
      status: task?.status || defaultStatus,
      timeEntries: task?.timeEntries || [],
      isTimerRunning: task?.isTimerRunning || false,
      currentTimerStart: task?.currentTimerStart,
    });

    onHide();
  };

  const addSubtask = () => {
    if (!newSubtaskTitle.trim()) return;
    setSubtasks([
      ...subtasks,
      {
        id: `sub-${Date.now()}`,
        title: newSubtaskTitle.trim(),
        done: false,
      },
    ]);
    setNewSubtaskTitle("");
  };

  const removeSubtask = (id: string) => {
    setSubtasks(subtasks.filter((s) => s.id !== id));
  };

  const toggleSubtask = (id: string) => {
    setSubtasks(
      subtasks.map((s) => (s.id === id ? { ...s, done: !s.done } : s))
    );
  };

  const projectOptions = [
    { value: "", label: "No Project", color: "#6c757d" },
    ...projects.map((proj) => ({
      value: proj.id,
      label: proj.name,
      color: proj.color,
    })),
  ];

  const selectedProject = projectOptions.find(
    (opt) => opt.value === (projectId || ""),
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="task-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`fas ${task ? "fa-edit" : "fa-plus-circle"}`}></i>
          {task ? "Edit Task" : "Create Task"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {/* Task Name Section */}
          <div className="modal-section">
            <Form.Group className="mb-0">
              <Form.Label className="mb-2">
                <i className="fas fa-heading"></i>
                Task Name *
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="What needs to be done?"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                className="task-name-input"
              />
            </Form.Group>
          </div>

          {/* Priority & Project Section */}
          <div className="modal-section">
            <div className="row mb-0">
              <div className="col-md-6">
                <Form.Group className="mb-0">
                  <Form.Label className="mb-2">
                    <i className="fas fa-flag"></i>
                    Priority
                  </Form.Label>
                  <Form.Select
                    value={priority}
                    onChange={(e) =>
                      setPriority(e.target.value as TaskPriority)
                    }
                    className="priority-select"
                  >
                    <option value="low">🟢 Low</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="high">🟠 High</option>
                    <option value="urgent">🔴 Urgent</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-0">
                  <Form.Label className="mb-2">
                    <i className="fas fa-folder"></i>
                    Project
                  </Form.Label>
                  <Select
                    value={selectedProject}
                    onChange={(option) => {
                      if (!option || option.value === "") {
                        setProjectId(null);
                      } else {
                        setProjectId(option.value);
                      }
                    }}
                    className="project-select-input"
                    options={projectOptions}
                    isClearable
                    placeholder="Choose a project..."
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        borderColor: "rgba(255, 255, 255, 0.1)",
                        minHeight: "48.5px",
                        borderRadius: "10px",
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: "#2c2c2e",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? "rgba(0, 122, 255, 0.2)"
                          : state.isSelected
                            ? "rgba(0, 122, 255, 0.3)"
                            : "transparent",
                        color: "#fff",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        "&:before": state.data.color
                          ? {
                              content: '""',
                              display: "inline-block",
                              width: "12px",
                              height: "12px",
                              borderRadius: "50%",
                              backgroundColor: state.data.color,
                              marginRight: "8px",
                            }
                          : {},
                      }),
                      singleValue: (base, state) => ({
                        ...base,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        "&:before":
                          state.data.color && state.data.value
                            ? {
                                content: '""',
                                display: "inline-block",
                                width: "10px",
                                height: "10px",
                                borderRadius: "50%",
                                backgroundColor: state.data.color,
                                marginRight: "6px",
                              }
                            : {},
                      }),
                      input: (base) => ({
                        ...base,
                        color: "#fff",
                      }),
                    }}
                  />
                </Form.Group>
              </div>
            </div>
          </div>

          {/* Estimation Section */}
          <div className="modal-section">
            <Form.Group className="mb-0">
              <Form.Label className="mb-2">
                <i className="fas fa-clock"></i>
                Time Estimation
              </Form.Label>
              <div className="estimation-input-group">
                <div className="quick-estimation-buttons">
                  {ESTIMATION_SHORTCUTS.map((shortcut) => (
                    <Button
                      key={shortcut.minutes}
                      variant={
                        estimationMinutes === shortcut.minutes
                          ? "primary"
                          : "outline-primary"
                      }
                      size="sm"
                      type="button"
                      onClick={() => setEstimationMinutes(shortcut.minutes)}
                      className="estimation-shortcut"
                    >
                      {shortcut.label}
                    </Button>
                  ))}
                </div>
                <div className="custom-estimation-input">
                  <Form.Control
                    type="number"
                    min="0"
                    step="5"
                    placeholder="Enter custom minutes"
                    value={estimationMinutes}
                    onChange={(e) => setEstimationMinutes(e.target.value)}
                    onFocus={(e) => e.target.select()}
                  />
                  <span className="input-suffix">minutes</span>
                </div>
              </div>
            </Form.Group>
          </div>

          {/* Description Section */}
          <div className="modal-section">
            <Form.Group className="mb-0">
              <Form.Label className="mb-2">
                <i className="fas fa-align-left"></i>
                Description
              </Form.Label>
              <CKEditor
                editor={ClassicEditor}
                config={{
                  licenseKey: "GPL",
                  plugins: [
                    Essentials,
                    Bold,
                    Italic,
                    Paragraph,
                    Undo,
                    Heading,
                    List,
                    Link,
                  ],
                  toolbar: [
                    "undo",
                    "redo",
                    "|",
                    "heading",
                    "|",
                    "bold",
                    "italic",
                    "|",
                    "numberedList",
                    "bulletedList",
                    "|",
                    "link",
                  ],
                }}
                data={descriptionHtml}
                onChange={(event, editor) => {
                  setDescriptionHtml(editor.getData());
                }}
              />
            </Form.Group>
          </div>

          {/* Subtasks Section */}
          <div className="modal-section">
            <Form.Group className="mb-0">
              <Form.Label className="mb-2">
                <i className="fas fa-tasks"></i>
                Subtasks
                {subtasks.length > 0 && (
                  <span className="subtask-count-badge">
                    {subtasks.filter((s) => s.done).length}/{subtasks.length}
                  </span>
                )}
              </Form.Label>
              <div className="subtasks-list mb-0">
                {subtasks.map((subtask) => (
                  <div key={subtask.id} className="subtask-item">
                    <Form.Check
                      type="checkbox"
                      checked={subtask.done}
                      onChange={() => toggleSubtask(subtask.id)}
                    />
                    <span
                      className="subtask-title"
                      style={{
                        textDecoration: subtask.done ? "line-through" : "none",
                        opacity: subtask.done ? 0.6 : 1,
                      }}
                    >
                      {subtask.title}
                    </span>
                    <Button
                      variant="link"
                      size="sm"
                      className="subtask-delete"
                      onClick={() => removeSubtask(subtask.id)}
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </div>
                ))}
              </div>
              <div className="subtask-add-group mb-0">
                <Form.Control
                  type="text"
                  placeholder="Type and press Enter to add..."
                  value={newSubtaskTitle}
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSubtask();
                    }
                  }}
                />
                <Button
                  variant="primary"
                  onClick={addSubtask}
                  disabled={!newSubtaskTitle.trim()}
                  className="subtask-add-btn"
                  title="Add subtask"
                >
                  <i className="fas fa-plus"></i>
                </Button>
              </div>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            <i className="fas fa-times"></i>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!name.trim()}>
            <i className={`fas ${task ? "fa-check" : "fa-plus"}`}></i>
            {task ? "Update Task" : "Create Task"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
